/**
 * 訂單毛利報表 Order Gross-Profit report (S11.1).
 *
 * 銷售金額 − 採購成本 = 毛利, one row per SO, drill-through to the SO detail
 * (plan §S11.1 accept: drill-through works). Purchase cost is the SO line
 * `landedCost` snapshot × qty (the persisted pricing-waterfall fact), so the
 * report reads the SAME cost the SO priced against — no re-derivation.
 *
 * Cross-currency totals are never summed into one number; the headline
 * aggregates PER CURRENCY, and rows with any un-costed line are shown as
 * "cost pending" rather than overstating profit.
 */

import { useMemo } from 'react';
import { Card, Col, Row, Statistic, Table, Tag, Tooltip, Typography, theme } from 'antd';
import type { TableProps } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TableContainer, { ListEmptyState, renderDash } from '@/components/common/TableContainer';
import { formatAmount, formatMoney } from '@/components/salesOrders/components/soFormat';
import type { SalesOrder } from '@/types/api/salesOrder';
import { useReportSalesOrders } from '../reportsData';
import { ReportSyncNote, ReportToolbar } from '../reportsShared';
import { exportReportCsv } from '../reportsCsv';

const { Text } = Typography;

interface MarginRow {
  soId: string;
  soNumber: string;
  customerName: string;
  status: SalesOrder['status'];
  currency: string;
  salesAmount: number;
  purchaseCost: number;
  grossProfit: number;
  marginPct: number;
  costKnown: boolean;
}

function toMarginRow(so: SalesOrder): MarginRow {
  const costKnown = so.lines.length > 0 && so.lines.every((line) => line.landedCost !== null);
  const purchaseCost = so.lines.reduce(
    (sum, line) => sum + (line.landedCost ?? 0) * line.qty,
    0
  );
  const salesAmount = so.totalAmount;
  const grossProfit = salesAmount - purchaseCost;
  return {
    soId: so.id,
    soNumber: so.soNumber,
    customerName: so.customerName,
    status: so.status,
    currency: so.currency,
    salesAmount,
    purchaseCost,
    grossProfit,
    marginPct: salesAmount > 0 ? (grossProfit / salesAmount) * 100 : 0,
    costKnown,
  };
}

const OrderMarginReport = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['reports', 'orders', 'common']);
  const { token } = theme.useToken();
  const { rows: salesOrders, isLoading, error, refresh } = useReportSalesOrders();

  const { rows, generatedAt } = useMemo(
    () => ({ rows: salesOrders.map(toMarginRow), generatedAt: new Date().toISOString() }),
    [salesOrders]
  );

  /** Per-currency profit roll-up over the costed rows (never cross-currency). */
  const totals = useMemo(() => {
    const map = new Map<string, { sales: number; cost: number; profit: number }>();
    rows
      .filter((row) => row.costKnown)
      .forEach((row) => {
        const entry = map.get(row.currency) ?? { sales: 0, cost: 0, profit: 0 };
        entry.sales += row.salesAmount;
        entry.cost += row.purchaseCost;
        entry.profit += row.grossProfit;
        map.set(row.currency, entry);
      });
    return [...map.entries()].map(([currency, value]) => ({ currency, ...value }));
  }, [rows]);

  const profitStyle = (value: number) => ({
    color: value < 0 ? token.colorError : token.colorText,
    fontVariantNumeric: 'tabular-nums' as const,
  });

  const columns: TableProps<MarginRow>['columns'] = [
    {
      title: t('reports:reports.orderMargin.columns.so'),
      key: 'so',
      width: 160,
      fixed: 'left',
      render: (_: unknown, row) => (
        <a
          style={{ color: token.colorPrimary, cursor: 'pointer', fontWeight: token.fontWeightStrong }}
          onClick={() => navigate(`/auth/sales-orders/${row.soId}`)}
        >
          {row.soNumber}
        </a>
      ),
    },
    {
      title: t('reports:reports.orderMargin.columns.customer'),
      dataIndex: 'customerName',
      key: 'customerName',
      width: 220,
      render: (value: string) => renderDash(value),
    },
    {
      title: t('reports:reports.orderMargin.columns.status'),
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status: MarginRow['status']) => <Tag>{t(`orders:so.status.${status}`)}</Tag>,
    },
    {
      title: t('reports:reports.orderMargin.columns.salesAmount'),
      key: 'salesAmount',
      width: 150,
      align: 'right',
      render: (_: unknown, row) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatMoney(row.currency, row.salesAmount)}
        </span>
      ),
    },
    {
      title: t('reports:reports.orderMargin.columns.purchaseCost'),
      key: 'purchaseCost',
      width: 150,
      align: 'right',
      render: (_: unknown, row) =>
        row.costKnown ? (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatMoney(row.currency, row.purchaseCost)}
          </span>
        ) : (
          <Tooltip title={t('reports:reports.orderMargin.costPendingHint')}>
            <Tag color="default">{t('reports:reports.orderMargin.costPending')}</Tag>
          </Tooltip>
        ),
    },
    {
      title: t('reports:reports.orderMargin.columns.grossProfit'),
      key: 'grossProfit',
      width: 150,
      align: 'right',
      render: (_: unknown, row) =>
        row.costKnown ? (
          <span style={profitStyle(row.grossProfit)}>
            {formatMoney(row.currency, row.grossProfit)}
          </span>
        ) : (
          '-'
        ),
    },
    {
      title: t('reports:reports.orderMargin.columns.marginPct'),
      key: 'marginPct',
      width: 110,
      align: 'right',
      render: (_: unknown, row) =>
        row.costKnown ? (
          <span style={profitStyle(row.grossProfit)}>{row.marginPct.toFixed(1)}%</span>
        ) : (
          '-'
        ),
    },
  ];

  const handleExport = () => {
    exportReportCsv<MarginRow>(
      'order-gross-profit.csv',
      [
        { header: t('reports:reports.orderMargin.columns.so'), value: (row) => row.soNumber },
        { header: t('reports:reports.orderMargin.columns.customer'), value: (row) => row.customerName },
        { header: t('reports:reports.orderMargin.columns.status'), value: (row) => t(`orders:so.status.${row.status}`) },
        { header: t('reports:reports.common.currency'), value: (row) => row.currency },
        { header: t('reports:reports.orderMargin.columns.salesAmount'), value: (row) => formatAmount(row.salesAmount) },
        { header: t('reports:reports.orderMargin.columns.purchaseCost'), value: (row) => (row.costKnown ? formatAmount(row.purchaseCost) : '') },
        { header: t('reports:reports.orderMargin.columns.grossProfit'), value: (row) => (row.costKnown ? formatAmount(row.grossProfit) : '') },
        { header: t('reports:reports.orderMargin.columns.marginPct'), value: (row) => (row.costKnown ? `${row.marginPct.toFixed(1)}%` : '') },
      ],
      rows
    );
  };

  return (
    <>
      <ReportToolbar
        formula={t('reports:reports.orderMargin.formula')}
        onExport={handleExport}
        exportDisabled={rows.length === 0}
      />

      <Row gutter={[12, 12]} style={{ marginBottom: token.marginLG }}>
        <Col xs={12} lg={6}>
          <Card size="small">
            <Statistic
              title={t('reports:reports.orderMargin.stats.orders')}
              value={rows.length}
            />
          </Card>
        </Col>
        {totals.map((entry) => (
          <Col xs={12} lg={6} key={entry.currency}>
            <Card size="small">
              <Statistic
                title={t('reports:reports.orderMargin.stats.grossProfit', { currency: entry.currency })}
                value={formatAmount(entry.profit)}
                valueStyle={{ color: entry.profit < 0 ? token.colorError : undefined }}
              />
              <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                {t('reports:reports.orderMargin.stats.salesCost', {
                  sales: formatAmount(entry.sales),
                  cost: formatAmount(entry.cost),
                })}
              </Text>
            </Card>
          </Col>
        ))}
      </Row>

      <TableContainer
        errorState={{
          active: Boolean(error),
          title: t('reports:reports.states.loadFailed'),
          retryLabel: t('common:errors.boundary.retry'),
          onRetry: refresh,
        }}
      >
        <Table<MarginRow>
          rowKey="soId"
          dataSource={rows}
          columns={columns}
          loading={isLoading}
          locale={{ emptyText: <ListEmptyState title={t('reports:reports.orderMargin.empty')} /> }}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          scroll={{ x: 1070 }}
        />
      </TableContainer>
      <ReportSyncNote syncedAt={generatedAt} />
    </>
  );
};

export default OrderMarginReport;
